import React, { useState, useEffect } from "react";
import FinalSettlementModal from "./FinalSettlementPageModal";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { fetchPayrollListForMonth } from "../../store/slices/payrollSlice";

const FinalSettlementPage: React.FC = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [year, setYear] = useState(now.getFullYear());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingData, setEditingData] = useState<any>(null);

  const dispatch = useDispatch<RootDispatch>();
  const settlements = useSelector((state: RootState) => state.payroll.fnfData);

  useEffect(() => {
    dispatch(fetchPayrollListForMonth({ month, year }));
    setIsModalOpen(false);
  }, [dispatch, month, year]);

  const [settlementLocks, setSettlementLocks] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (Array.isArray(settlements)) {
      const initialLocks: Record<string, boolean> = {};
      settlements.forEach((s) => {
        initialLocks[s.userId] = s.settlementLocked ?? false;
      });
      setSettlementLocks(initialLocks);
    }
  }, [settlements]);

  const toggleLock = (userId: string) => {
    setSettlementLocks((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const openModalForNew = () => {
    setEditingData(null);
    setIsModalOpen(true);
  };

  const openModalForEdit = (s: any) => {
    const editData = {
      userId: s.userId,
      resignationSubmittedOn: formatDate(s.resignationSubmittedOn),
      leavingDate: formatDate(s.leavingDate),
      leavingReason: s.reasonForLeaving || "",
      settlementDate: formatDate(s.settlementDate),
      noticeRequired: s.noticeRequired || false,
      noticePeriod: s.noticePeriod || 0,
      daysServed: s.noticePeriod
        ? s.noticePeriod - (s.shortFallInNoticePeriod || 0)
        : 0,
      payrollMonth: s.month || "",
      workDays: s.workDays || 0,
      daysWorked: s.daysWorked || 0,
      leaveEncashment: s.encashableLeaves?.length
        ? s.encashableLeaves
        : [
            { type: "cof", balance: 0, encashed: 0 },
            { type: "pl", balance: 0, encashed: 0 },
          ],
      employeeName: s.employeeName || "N/A",
      employeeNo: s.employeeNo || "N/A",
    };
    setEditingData(editData);
    setIsModalOpen(true);
  };

  // Consolidated formatDate function
  const formatDate = (date?: string) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return isNaN(d.getTime()) ? "N/A" : d.toISOString().split("T")[0];
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  function goPrevMonth() {
    const d = new Date(year, month - 2, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  }

  function goNextMonth() {
    const d = new Date(year, month, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  }

  return (
    <div className="w-full h-full flex flex-col gap-4 p-4">
      {/* Header with filters - all on the left side */}
      <div className="flex items-center justify-between gap-3 ml-20">
        <div className="flex items-center gap-2">
          <button
            onClick={goPrevMonth}
            className="px-3 py-2 rounded-xl bg-white shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
          >
            ◀
          </button>
          <div className="text-lg font-semibold text-slate-800 min-w-[180px] text-center">
            {monthNames[month - 1]} {year}
          </div>
          <button
            onClick={goNextMonth}
            className="px-3 py-2 rounded-xl bg-white shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
          >
            ▶
          </button>
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
            className="px-3 py-2 rounded-xl bg-white shadow-sm ring-1 ring-slate-200"
          >
            {monthNames.map((mName, idx) => (
              <option key={mName} value={idx + 1}>
                {mName}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={year}
            onChange={(e) =>
              setYear(parseInt(e.target.value || String(now.getFullYear())))
            }
            className="w-28 px-3 py-2 rounded-xl bg-white shadow-sm ring-1 ring-slate-200"
          />
        </div>
        <div className="flex justify-end mr-16">
          <button
            onClick={openModalForNew}
            className="rounded-lg px-3 py-2.5 text-sm transition-all bg-navyBlue-primary text-white font-medium hover:bg-blue-500 active:scale-95 focus:ring-2 focus:ring-blue-300 max-w-fit"
          >
            Settle Employee
          </button>
        </div>
      </div>
      {isModalOpen && (
        <FinalSettlementModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          editingData={editingData}
        />
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-sm text-center">
          <thead className="sticky top-0 z-10 bg-slate-50">
            <tr>
              <th className="px-4 py-2 font-semibold text-slate-700 border border-slate-200">
                Payout Month
              </th>
              <th className="px-4 py-2 font-semibold text-slate-700 border border-slate-200">
                Emp ID
              </th>
              <th className="px-4 py-2 font-semibold text-slate-700 border border-slate-200">
                Emp Name
              </th>
              <th className="px-4 py-2 font-semibold text-slate-700 border border-slate-200">
                Leaving Date
              </th>
              <th className="px-4 py-2 font-semibold text-slate-700 border border-slate-200">
                Net Pay
              </th>
              <th className="px-4 py-2 font-semibold text-slate-700 border border-slate-200">
                Processed On
              </th>
              <th className="px-4 py-2 font-semibold text-slate-700 border border-slate-200">
                Settlement Date
              </th>
              <th className="px-4 py-2 font-semibold text-slate-700 border border-slate-200">
                Lock/Unlock
              </th>
              <th className="px-4 py-2 font-semibold text-slate-700 border border-slate-200">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(settlements) && settlements.length > 0 ? (
              settlements.map((s: any, index: number) => (
                <tr
                  key={s.employeeId || s.userId || index}
                  className="odd:bg-white even:bg-slate-50"
                >
                  <td className="border border-slate-200 px-4 py-2">
                    {s.month}
                  </td>
                  <td className="border border-slate-200 px-4 py-2">
                    {s.employeeNo || "N/A"}
                  </td>
                  <td className="border border-slate-200 px-4 py-2">
                    {s.employeeName || "N/A"}
                  </td>
                  <td className="border border-slate-200 px-4 py-2">
                    {formatDate(s.leavingDate)}
                  </td>
                  <td className="border border-slate-200 px-4 py-2">
                    {s.employeeBenefits?.netSalary ?? "N/A"}
                  </td>
                  <td className="border border-slate-200 px-4 py-2">
                    {formatDate(s.processedOn)}
                  </td>
                  <td className="border border-slate-200 px-4 py-2">
                    {formatDate(s.settlementDate)}
                  </td>
                  <td className="border border-slate-200 px-4 py-2">
                    <div className="flex items-center justify-center">
                      <button
                        onClick={() => toggleLock(s.userId)}
                        className={`h-8 w-8 flex items-center justify-center text-lg rounded-md ${
                          settlementLocks[s.userId]
                            ? "bg-red-100 hover:bg-red-200"
                            : "bg-green-100 hover:bg-green-200"
                        } ring-1 ring-slate-200`}
                        title={settlementLocks[s.userId] ? "Unlock" : "Lock"}
                      >
                        {settlementLocks[s.userId] ? "🔒" : "🔓"}
                      </button>
                    </div>
                  </td>
                  <td className="border border-slate-200 px-4 py-2">
                    <div className="flex items-center justify-center gap-3">
                      <button className="text-green-600 hover:text-green-800">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => openModalForEdit(s)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-slate-500">
                  No records found for {monthNames[month - 1]} {year}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinalSettlementPage;